import React, { useCallback, useEffect, useState } from "react"
import { useAppContext } from "@ciq/shared/hooks/context"
import { Button } from "@ciq/shared/components/buttons"
import { EntryModal } from "@ciq/shared/components/modal"
import { ErrorToast } from "@ciq/shared/utils/toast"
import { InlineIndicator } from "@ciq/shared/components/loader"
import { ReactComponent as AlertIcon } from "@ciq/shared/assets/icons/alert_icon.svg"
import { getActiveJob, ProcessingSummary, useJobPolling, ZoneUploadManager } from "./zoneUploads"
import { cancelJob } from "./zoneUploads/api"
import Divider from "@ciq/shared/components/divider"

/**
 * Confirmation modal for canceling a pending upload job.
 *
 * @param {Object} props - Component props
 * @param {boolean} props.show - Whether to show the modal
 * @param {Function} props.onHide - Callback when modal is closed
 * @param {Function} props.onConfirm - Callback when user confirms cancellation
 * @returns {React.ReactElement} The modal component
 */
function ConfirmCancelModal ({ show, onHide, onConfirm }) {
  const buttons = {
    secondary: {
      title: "Keep Upload",
      onClick: onHide
    },
    danger: {
      title: "Cancel Upload",
      onClick: onConfirm
    }
  }

  return (
    <EntryModal show={show} title="Cancel Incomplete Upload?" onHide={onHide} buttons={buttons}>
      <p>This will permanently cancel the incomplete upload. You can start a new upload after
        canceling.</p>
    </EntryModal>
  )
}

/**
 * @typedef {Object} DataUploadsTabProps
 * @property {boolean} show - Boolean indicating if the tab is visible
 * @property {boolean} canManageSchool - Boolean indicating if user has school management permissions
 */

/**
 * Data Uploads tab for the School page.
 * Manages zone data upload workflow.
 * Fetches active job on mount to show appropriate view.
 *
 * @param {DataUploadsTabProps} props - Component props
 * @returns {React.ReactElement|null} The tab component or null if not shown
 */
export function DataUploadsTab ({ show, canManageSchool }) {
  const { schoolId } = useAppContext()
  const [viewState, setViewState] = useState("loading") // loading | upload | pending | processing | results | error
  const [activeJobId, setActiveJobId] = useState(null)
  const [activeJobStatus, setActiveJobStatus] = useState(null)
  const [error, setError] = useState(null)
  const [isCancelling, setIsCancelling] = useState(false)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [summaryData, setSummaryData] = useState(null)

  // Polling hook - only active when in processing state
  const { job: polledJob, isPolling, error: pollingError } = useJobPolling(
    schoolId,
    viewState === "processing" ? activeJobId : null
  )

  // Fetch active job on mount
  useEffect(() => {
    if (!show || !canManageSchool || !schoolId) {
      return
    }

    let mounted = true

    async function fetchActiveJob () {
      try {
        const activeJob = await getActiveJob(schoolId)

        if (!mounted) {
          return
        }

        if (activeJob) {
          setActiveJobId(activeJob.job_id)
          setActiveJobStatus(activeJob.status)

          // Determine view based on status
          if (activeJob.status === "PENDING") {
            // Job created but not submitted - show pending view to cancel or resume
            setViewState("pending")
          } else if (["SUBMITTED", "IN_PROGRESS", "CANCELLING"].includes(activeJob.status)) {
            setViewState("processing")
          } else {
            // Terminal states (COMPLETED, CANCELLED, ERROR) - allow new uploads
            setViewState("upload")
          }
        } else {
          setViewState("upload")
        }
      } catch (err) {
        if (!mounted) {
          return
        }
        console.error("Failed to fetch active job:", err)
        setError("Failed to load upload status. Please refresh the page.")
        setViewState("error")
      }
    }

    fetchActiveJob()

    return () => {
      mounted = false
    }
  }, [show, canManageSchool, schoolId])

  // Handle job completion from polling
  useEffect(() => {
    if (!polledJob) {
      return
    }

    // Update status
    setActiveJobStatus(polledJob.status)

    // Handle completion - use results directly from backend
    if (polledJob.status === "COMPLETED" && polledJob.results) {
      setSummaryData(polledJob.results)
      setViewState("results")
    } else if (polledJob.status === "COMPLETED" && !polledJob.results) {
      // Job completed but no results available (e.g., S3 read failed)
      setViewState("error")
      setError("Processing completed but results are not available. Please try uploading again.")
    }

    // Handle error state
    if (polledJob.status === "ERROR") {
      setViewState("error")
      // Backend returns standardized error_details: { code, message }
      const details = polledJob.error_details
      const errorMessage = details?.message || "Processing failed. Please try again."
      setError(errorMessage)
    }

    // Handle cancelled state
    if (polledJob.status === "CANCELLED") {
      setActiveJobId(null)
      setActiveJobStatus(null)
      setViewState("upload")
    }
  }, [polledJob])

  // Handle polling errors
  useEffect(() => {
    if (pollingError) {
      setError(pollingError)
      setViewState("error")
    }
  }, [pollingError])

  const handleProcessingStart = useCallback((jobId) => {
    setActiveJobId(jobId)
    setActiveJobStatus("SUBMITTED")
    setViewState("processing")
  }, [])

  const handleShowConfirmModal = useCallback(() => {
    setShowConfirmModal(true)
  }, [])

  const handleHideConfirmModal = useCallback(() => {
    setShowConfirmModal(false)
  }, [])

  const handleCancelPendingJob = useCallback(async () => {
    if (!activeJobId) {
      return
    }

    setIsCancelling(true)
    setShowConfirmModal(false)
    try {
      await cancelJob(schoolId, activeJobId)
      setActiveJobId(null)
      setActiveJobStatus(null)
      setViewState("upload")
    } catch (err) {
      console.error("Failed to cancel pending job:", err)
      ErrorToast("Failed to cancel the pending upload. Please try again.")
    } finally {
      setIsCancelling(false)
    }
  }, [schoolId, activeJobId])

  if (!show || !canManageSchool) {
    return null
  }

  // Loading state
  if (viewState === "loading") {
    return (
      <div className="school-data-uploads-tab">
        <div className="school-data-uploads-loading">
          Loading...
        </div>
      </div>
    )
  }

  // Error state - shows error banner above upload UI
  if (viewState === "error") {
    return (
      <div className="school-data-uploads-tab">
        <div className="zone-upload-manager__error">
          <span>{error}</span>
          <button
            className="zone-upload-manager__error-dismiss"
            onClick={() => {
              setError(null)
              setActiveJobId(null)
              setActiveJobStatus(null)
              setViewState("upload")
            }}
            aria-label="Dismiss error"
          >
            &times;
          </button>
        </div>
        <ZoneUploadManager
          onProcessingStart={handleProcessingStart}
        />
      </div>
    )
  }

  // Pending state - orphaned job that needs to be cancelled
  if (viewState === "pending") {
    return (
      <div className="school-data-uploads-tab">
        <div className="school-data-uploads-pending">
          <div className="school-data-uploads-pending__icon">
            <AlertIcon className="red-color"/>
          </div>
          <h3 className="school-data-uploads-pending__title">Incomplete Upload Found</h3>
          <p className="school-data-uploads-pending__message">
            A previous upload was started but not completed.
            Cancel it to start a new upload.
          </p>
          <Button
            variant="danger"
            onClick={handleShowConfirmModal}
            disabled={isCancelling}
          >
            {isCancelling ? "Cancelling..." : "Cancel Previous Upload"}
          </Button>
        </div>
        <ConfirmCancelModal
          show={showConfirmModal}
          onHide={handleHideConfirmModal}
          onConfirm={handleCancelPendingJob}
        />
      </div>
    )
  }

  // Processing view - shows spinner while polling
  if (viewState === "processing") {
    return (
      <div className="school-data-uploads-tab">
        <div className="school-data-uploads-processing" role="status" aria-live="polite">
          <InlineIndicator show={true}/>
          <p className="school-data-uploads-processing__message">
            Processing files... This may take a few moments.
          </p>
          {activeJobStatus && (
            <p className="school-data-uploads-processing__status">
              Status: {activeJobStatus}
            </p>
          )}
        </div>
      </div>
    )
  }

  // Results view - shows processing summary
  if (viewState === "results" && summaryData) {
    return (
      <div className="school-data-uploads-tab">
        <ProcessingSummary data={summaryData}/>
        <Divider/>
        <div className="school-data-uploads-actions">
          <Button
            variant="secondary"
            onClick={() => {
              setSummaryData(null)
              setActiveJobId(null)
              setViewState("upload")
            }}
          >
            Cancel
          </Button>
          <div>
            <Button
              variant="primary"
              onClick={() => {
                setSummaryData(null)
                setActiveJobId(null)
                setViewState("upload")
              }}
            >
              Approve & Upload
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="school-data-uploads-tab">
      <ZoneUploadManager
        onProcessingStart={handleProcessingStart}
      />
    </div>
  )
}
